import { InvoiceClientWorkPageSchema, SwitchInvoiceClientWithWorkResponseSchema, INVOICE_NULLABLE_CLIENT_HEADERS, InvoiceListResponseSchema, InvoiceWithLineItemsSchema, InvoiceSchema, ApproveInvoiceResponseSchema, CreateInvoiceResponseSchema, DeleteInvoiceResponseSchema, InvoiceSettingsResponseSchema, ListInvoiceSendsResponseSchema, EligibleInvoiceAttachmentsResponseSchema, ListInvoiceCorrectionsResponseSchema, InvoicesByLineItemResponseSchema, InvoiceAggregateResponseSchema, } from '@packages/types';
import { api, apiRaw, filenameFromResponse } from '../client.js';
import { invoiceListFilterQuery } from './invoice-list-export.js';
export function listInvoices(accountId, params) {
    const qs = invoiceListFilterQuery(params);
    if (params?.cursor)
        qs.set('cursor', params.cursor);
    if (params?.limit != null)
        qs.set('limit', String(params.limit));
    for (const id of params?.ids ?? [])
        qs.append('ids', id);
    const query = qs.toString();
    return api(`/accounts/${accountId}/invoices${query ? `?${query}` : ''}`, InvoiceListResponseSchema, { headers: INVOICE_NULLABLE_CLIENT_HEADERS });
}
/**
 * Invoice counts and totals. The route counts approved, sent and paid invoices
 * unless `status` names more, so a caller that wants voided ones lists them.
 */
export function aggregateInvoices(accountId, query) {
    const params = new URLSearchParams();
    for (const status of query?.status ?? [])
        params.append('status', status);
    if (query?.clientId)
        params.set('clientId', query.clientId);
    if (query?.clientProjectId)
        params.set('clientProjectId', query.clientProjectId);
    if (query?.dateFrom)
        params.set('dateFrom', query.dateFrom);
    if (query?.dateTo)
        params.set('dateTo', query.dateTo);
    if (query?.groupBy)
        params.set('groupBy', query.groupBy);
    if (query?.thenBy)
        params.set('thenBy', query.thenBy);
    const qs = params.toString();
    return api(`/accounts/${accountId}/invoices/aggregate${qs ? `?${qs}` : ''}`, InvoiceAggregateResponseSchema);
}
export function getInvoice(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}`, InvoiceWithLineItemsSchema, { headers: INVOICE_NULLABLE_CLIENT_HEADERS });
}
export function createInvoice(accountId, input) {
    return api(`/accounts/${accountId}/invoices`, CreateInvoiceResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function updateInvoice(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}`, InvoiceSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function deleteInvoice(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}`, DeleteInvoiceResponseSchema, {
        method: 'DELETE',
    });
}
export function addLineItem(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
export function moveLineItem(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/move`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
export function removeLineItem(accountId, invoiceId, lineItemId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/${lineItemId}`, InvoiceWithLineItemsSchema, {
        method: 'DELETE',
    });
}
export function allocateInvoiceLineDefaults(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/allocate-defaults`, InvoiceWithLineItemsSchema, { method: 'POST', body: {} });
}
export function allocateInvoiceLineItem(accountId, invoiceId, lineItemId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/${lineItemId}/allocate`, DeleteInvoiceResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function updateLineItem(accountId, invoiceId, lineItemId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/${lineItemId}`, InvoiceWithLineItemsSchema, { method: 'PATCH', body: input });
}
/**
 * Set the order of one section's lines (fees or disbursements) on one day of a
 * draft invoice. Sends the full desired order, so a retry leaves the same order.
 */
export function reorderLineItemsForDay(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/day-order`, InvoiceWithLineItemsSchema, {
        method: 'PUT',
        body: input,
    });
}
export function removeLineItems(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/batch-remove`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
export function addDirectLineItem(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/direct`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
/**
 * Pull an approved supplier invoice (expense) onto a draft invoice as a
 * disbursement line. The backend snapshots the supplier name, reference,
 * subtotal, and VAT rate onto the new line item and stores the FK.
 *
 * Returns HTTP 400 when the SI is not APPROVED, 409 when it is already on a
 * line (including concurrent adds caught by the @unique DB constraint).
 * Idempotent when called again with the same `idempotencyKey`.
 */
export function addLineItemFromExpense(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/line-items/from-expense`, InvoiceWithLineItemsSchema, { method: 'POST', body: input });
}
export function addDiscountLine(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/discount-lines`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
export function approveInvoice(accountId, invoiceId, options) {
    const body = {};
    if (options?.bankAccountId !== undefined)
        body.bankAccountId = options.bankAccountId;
    if (options?.subjectLine !== undefined)
        body.subjectLine = options.subjectLine;
    return api(`/accounts/${accountId}/invoices/${invoiceId}/approve`, ApproveInvoiceResponseSchema, {
        method: 'POST',
        body,
    });
}
export function sendInvoice(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/send`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input ?? {},
    });
}
/**
 * Duplicate an existing invoice into a fresh editable DRAFT (v1 of recurring
 * invoices). Copies the client, fee and manual lines, per-line VAT, and notes;
 * resets the date to today and allocates a new invoice number. Expense lines
 * (sourced from a supplier invoice) and time-entry links are deliberately not
 * copied. The idempotency key is synthesised per call.
 */
export function copyInvoice(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/copy`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: { idempotencyKey: crypto.randomUUID() },
    });
}
/**
 * Void an APPROVED invoice and recreate it as an editable DRAFT in one call -
 * the correction path for a line-item mistake noticed after approval. Every
 * line carries verbatim (linked time entries and expenses re-linked, original
 * dates kept) and a new invoice number is allocated; the caller lands in the
 * new draft to fix the wrong line and re-approve. The idempotency key is
 * synthesised per call.
 */
export function reissueInvoice(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/reissue`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: { idempotencyKey: crypto.randomUUID() },
    });
}
export function listInvoiceSends(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/sends`, ListInvoiceSendsResponseSchema);
}
export function getEligibleInvoiceAttachments(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/eligible-attachments`, EligibleInvoiceAttachmentsResponseSchema);
}
export function markInvoiceSent(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/mark-sent`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: {},
    });
}
export function voidInvoice(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/void`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: {},
    });
}
export async function downloadInvoicePdf(accountId, invoiceId, options) {
    const params = new URLSearchParams();
    if (options?.bankAccountId)
        params.set('bankAccountId', options.bankAccountId);
    // subjectLine: undefined = no override (use stored toggle gate); null = toggle off (empty string on the wire); string = render as-is.
    if (options && 'subjectLine' in options && options.subjectLine !== undefined) {
        params.set('subjectLine', options.subjectLine ?? '');
    }
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    const response = await apiRaw(`/accounts/${accountId}/invoices/${invoiceId}/pdf${qs}`);
    const filename = filenameFromResponse(response, 'invoice.pdf');
    const blob = await response.blob();
    return { blob, filename };
}
/**
 * Atomically create a pro forma invoice record and download the PDF.
 * Each call allocates the next PF-NNNN number and inserts a proforma_invoices row.
 *
 * `subjectLine` mirrors the DRAFT preview semantics: undefined = use the
 * stored toggle gate on the invoice; null = hide the matter row; a string is
 * rendered verbatim. Used to forward the editor's live toggle state so the
 * pro forma PDF matches what the user sees in the preview.
 */
export async function generateProformaInvoicePdf(accountId, invoiceId, options) {
    const params = new URLSearchParams();
    if (options && 'subjectLine' in options && options.subjectLine !== undefined) {
        params.set('subjectLine', options.subjectLine ?? '');
    }
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    const response = await apiRaw(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices${qs}`, { method: 'POST' });
    const filename = filenameFromResponse(response, 'proforma-invoice.pdf');
    const blob = await response.blob();
    return { blob, filename };
}
/**
 * Atomically create a pro forma invoice record and download the Word document.
 * Each call allocates the next PF-NNNN number, inserts a proforma_invoices row,
 * and embeds the pro forma number in the DOCX body.
 *
 * `subjectLine` is forwarded to the PDF path only; for DOCX the subject line is
 * already stored on the invoice and rendered from the stored value. Pass
 * `options.subjectLine` if you want to forward the editor's live toggle state
 * (same semantics as generateProformaInvoicePdf).
 *
 * `statementExtras` adds optional post-VAT display rows (deposit, brought-forward)
 * to the C&A Friedlander Word export. Display-only; never affects invoice totals,
 * ledger, Xero, or PDF. Always sends Content-Type: application/json with at
 * minimum an empty JSON body so the route's optional body schema parses correctly.
 */
export async function generateProformaInvoiceDocx(accountId, invoiceId, options) {
    const params = new URLSearchParams({ format: 'docx' });
    if (options && 'subjectLine' in options && options.subjectLine !== undefined) {
        params.set('subjectLine', options.subjectLine ?? '');
    }
    // Always send a JSON body so the route receives Content-Type: application/json.
    // An empty object is the minimum; statementExtras is included when provided.
    const bodyPayload = {};
    if (options?.statementExtras !== undefined) {
        bodyPayload.statementExtras = options.statementExtras;
    }
    const response = await apiRaw(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices?${params.toString()}`, { method: 'POST', body: bodyPayload });
    const filename = filenameFromResponse(response, 'proforma-invoice.docx');
    const blob = await response.blob();
    return { blob, filename };
}
/**
 * Record a payment allocation against an invoice.
 * The caller must generate `idempotencyKey` (e.g. via `crypto.randomUUID()`) at submit time
 * so that a retried POST returns the same invoice rather than creating a duplicate payment.
 */
export function recordInvoicePayment(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/payments`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
/**
 * Void a single recorded payment allocation. Returns the updated invoice. The
 * allocation is removed immediately, but the stored PAID status reverts to SENT
 * asynchronously (the payment.voided listener), so callers should invalidate the
 * invoice queries and refetch rather than rely on the response showing the
 * reverted lifecycle status.
 */
export function voidInvoicePayment(accountId, invoiceId, paymentId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/payments/${paymentId}/void`, InvoiceWithLineItemsSchema, { method: 'POST', body: {} });
}
export function writeOffInvoice(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/write-off`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
export function getInvoiceSettings(accountId) {
    return api(`/accounts/${accountId}/invoice-settings`, InvoiceSettingsResponseSchema);
}
export function updateInvoiceSettings(accountId, input) {
    return api(`/accounts/${accountId}/invoice-settings`, InvoiceSettingsResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
/**
 * Correct non-financial particulars (client/firm/bank identity, reference,
 * matter name, notes, payment reference) on an issued (APPROVED/SENT/PAID)
 * invoice in place - VAT Act s20(1B). `input.changes` should carry only the
 * fields that actually changed; the caller must generate `idempotencyKey`
 * (e.g. via `crypto.randomUUID()`) once per correction attempt.
 */
export function correctInvoiceParticulars(accountId, invoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/corrections`, InvoiceWithLineItemsSchema, {
        method: 'POST',
        body: input,
    });
}
/** Newest-first correction history for an issued invoice. */
export function listInvoiceCorrections(accountId, invoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/corrections`, ListInvoiceCorrectionsResponseSchema);
}
/**
 * Download the PDF copy retained at the moment of a specific correction (the
 * last document actually delivered before that correction landed). Only call
 * when the correction's `hasPriorPdf` is true - the route 404s otherwise.
 */
export async function downloadInvoiceCorrectionPdf(accountId, invoiceId, correctionId) {
    const response = await apiRaw(`/accounts/${accountId}/invoices/${invoiceId}/corrections/${correctionId}/pdf`);
    const filename = filenameFromResponse(response, 'invoice.pdf');
    const blob = await response.blob();
    return { blob, filename };
}
/**
 * Email an already-generated PDF pro forma to the client: its stored document,
 * exactly as generated - nothing is minted or re-rendered, and the underlying
 * invoice's status and line items are untouched. Refused for a Word pro forma,
 * or once the invoice has changed since the pro forma was generated. The
 * caller must generate `idempotencyKey` (e.g. via `crypto.randomUUID()`) at
 * submit time so a retried POST does not send a duplicate email.
 */
export function sendProformaInvoice(accountId, invoiceId, proformaInvoiceId, input) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices/${proformaInvoiceId}/send`, InvoiceWithLineItemsSchema, { method: 'POST', body: input });
}
/**
 * Attest that a pro forma was already delivered to the client outside Bill
 * (printed, hand delivered, sent from a personal inbox). Idempotent: calling
 * this again on an already-sent pro forma is a no-op.
 */
export function markProformaSent(accountId, invoiceId, proformaInvoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices/${proformaInvoiceId}/mark-sent`, InvoiceWithLineItemsSchema, { method: 'POST', body: {} });
}
/**
 * Withdraw a live (sent) pro forma, recording that the firm no longer stands
 * behind it and unfreezing the underlying draft's line items. The document
 * and its PF number are kept, never deleted. Idempotent: calling this again
 * on an already-withdrawn pro forma is a no-op.
 */
export function withdrawProforma(accountId, invoiceId, proformaInvoiceId) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices/${proformaInvoiceId}/withdraw`, InvoiceWithLineItemsSchema, { method: 'POST', body: {} });
}
/** Download a pro forma's stored document (whatever format it was generated in). */
export async function downloadProformaDocument(accountId, invoiceId, proformaInvoiceId) {
    const response = await apiRaw(`/accounts/${accountId}/invoices/${invoiceId}/proforma-invoices/${proformaInvoiceId}/document`);
    const filename = filenameFromResponse(response, 'proforma-invoice.pdf');
    const blob = await response.blob();
    return { blob, filename };
}
/**
 * Resolve which invoice each of the given line-item ids sits on, and return
 * those invoices in full (liveProforma included). Capped at 100 ids per call
 * by the route; a caller with more ids must batch.
 */
export function listInvoicesByLineItem(accountId, lineItemIds) {
    const qs = new URLSearchParams();
    for (const id of lineItemIds)
        qs.append('lineItemIds', id);
    return api(`/accounts/${accountId}/invoices/by-line-item?${qs.toString()}`, InvoicesByLineItemResponseSchema);
}
export function setInvoiceClient(accountId, invoiceId, body) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/client`, InvoiceSchema, {
        method: 'PUT', body,
    });
}
export function getInvoiceClientWork(accountId, invoiceId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.set('cursor', params.cursor);
    if (params?.limit != null)
        query.set('limit', String(params.limit));
    const suffix = query.toString();
    return api(`/accounts/${accountId}/invoices/${invoiceId}/client-work${suffix ? `?${suffix}` : ''}`, InvoiceClientWorkPageSchema);
}
export function switchInvoiceClientWithWork(accountId, invoiceId, body) {
    return api(`/accounts/${accountId}/invoices/${invoiceId}/client-with-work`, SwitchInvoiceClientWithWorkResponseSchema, { method: 'PUT', body });
}
